import { useQuery } from '@apollo/client';
import { phoenixLegalMemberDetailsQuery } from '@marriott/mi-legal-graphql';
import { InputTextField } from '@marriott/mi-ui-library';
import clsx from 'clsx';
import { Controller, useFormContext } from 'react-hook-form';
import { MemberDetailResponse } from '../../molecules/ContactInformation/ContactInformation.types';
import { useAuthStore, useLegalGenericStore } from '../../store';
import { scrollToTop } from '../../utils/scrollUtils';
import { generateApolloClientHeadersForUXLCalls } from '../../utils/uxlHelper';
import { FormData, BrgModelProps } from './BrgClaimForm.types';

const GET_MEMBERS_OPERATION_NAME = 'phoenixLegalMemberDetailsQuery';

interface BrgGuestInfoProps {
  guestInformation: BrgModelProps['guestInformation'];
  showAPIAlert: () => void;
}

export function BrgGuestInfo({ showAPIAlert, guestInformation }: BrgGuestInfoProps) {
  const { reset } = useFormContext<FormData>();

  const { language, sessionData } = useLegalGenericStore();
  const { sessionId } = useAuthStore();

  const { loading: memberLoading } = useQuery<MemberDetailResponse>(phoenixLegalMemberDetailsQuery, {
    variables: { customerId: sessionData?.cacheData?.data?.consumerID },
    context: generateApolloClientHeadersForUXLCalls(sessionId, GET_MEMBERS_OPERATION_NAME, language),
    onCompleted(memberData) {
      reset({
        firstName: memberData?.customer?.profile?.name?.givenName || 'NA',
        lastName: memberData?.customer?.profile?.name?.surname || 'NA',
        email: memberData?.customer?.contactInformation?.emails[0]?.address || 'NA',
        marriottRewardsNumber: memberData?.customer?.loyaltyInformation?.rewards?.number || 'NA',
      });
    },
    skip: !sessionData?.cacheData?.data?.consumerID,
    onError(error) {
      console.error(`Something went wrong while fetching member details: ${JSON.stringify(error)}`);
      reset({
        firstName: 'NA',
        lastName: 'NA',
        email: 'NA',
        marriottRewardsNumber: 'NA',
      });
      showAPIAlert();
      scrollToTop();
    },
  });

  return (
    <div id="guest-info" className="max-w-brg">
      <h2>{guestInformation.componentTitle}</h2>
      <fieldset disabled>
        <div className="m-form-group">
          <RenderInputField name="firstName" label={guestInformation.firstName.label} memberLoading={memberLoading} />
          <RenderInputField name="lastName" label={guestInformation.lastName.label} memberLoading={memberLoading} />
          <RenderInputField name="email" label={guestInformation.email.label} memberLoading={memberLoading} />
          <RenderInputField
            name="marriottRewardsNumber"
            label={guestInformation.memberNumber.label}
            memberLoading={memberLoading}
          />
        </div>
      </fieldset>
    </div>
  );
}

type GuestInfoFields = Pick<FormData, 'firstName' | 'lastName' | 'email' | 'marriottRewardsNumber'>;
interface RenderInputFieldProps {
  name: keyof GuestInfoFields;
  label: string;
  memberLoading: boolean;
}
const RenderInputField = ({ name, label, memberLoading }: RenderInputFieldProps) => {
  const { control } = useFormContext<FormData>();

  return (
    <Controller
      name={name}
      control={control}
      rules={{ required: true }}
      render={({ field }) => (
        <InputTextField
          label={label}
          inputValue={field.value ?? ''} // !NOTE: when we reset the value become undefined and this become controlled to uncontrolled
          className={clsx('m-input-field', {
            'skeleton-loader': memberLoading,
            'is-disabled': true,
          })}
          inputTextFieldClassName="is-disabled"
        />
      )}
    />
  );
};
